import { Nullable } from '@app/types';
import {
  NotificationLevel,
  WebhookAuthType,
  WebhookEvents,
} from '@const/consts';
import { IEnvelopeUser } from '../envelopes/types';

export interface IBoxesInitialValues {
  webHook: ICurrentWebHook;
  boxes: IBox[];
}

export interface IWebHook {
  isWebhookEnabled: boolean;
  webhookUrl: Nullable<string>;
  webhookAuthType: Nullable<WebhookAuthType>;
  webhookAuthToken: Nullable<string>;
  webhookEvents: Nullable<Array<Nullable<WebhookEvents>>>;
}

export interface ICurrentWebHook extends IWebHook {
  lastCallInfo: ILastCallInfo;
}

export interface ILastCallInfo {
  timestamp: string;
  statusCode: string;
  statusMessage: Nullable<string>;
}

// Тип ящика
export enum IBoxType {
  PERSONAL = 'PERSONAL',
  BUSINESS = 'BUSINESS',
}

// Роль пользователя в ящике
export enum IBoxRole {
  VIEWER = 'VIEWER',
  ADMIN = 'ADMIN',
  MANAGER = 'MANAGER',
}

// Роль пользователя в ящике
export interface IBoxMain {
  /** Электронная почта */
  email: string;
  /** Наименование организации */
  companyName: string;
  /** Почтовый индекс */
  companyZip?: Nullable<string>;
  /** Почтовый индекс */
  companyAddress?: Nullable<string>;
}

// Интерфейс для создания ящика
export interface IBoxCreate extends IBoxMain {
  position?: Nullable<string>;
}

// Интерфейс для создания ящика
export interface IBox extends IBoxMain {
  /** Тип ящика */
  type: IBoxType;
  /** oguid бокса */
  oguid: string;
  /** Роль пользователя */
  role: IBoxRole;
  /** Флаг доступа к ящику только на чтение */
  isReadOnlyEmail: boolean;
  /** Дата и время создания */
  created?: Nullable<string>;
  /** Краткая структура с пользователем */
  createdBy?: IEnvelopeUser;
  /** Количество активированных пользователей в ящике */
  usersActivated?: number;
  /** Количество пользователей в ящике, ожидающий активации */
  usersWaiting?: number;
  /** Лого текущего бокса */
  logo?: Nullable<string>;
}

// Интерфейс данных для обновления ящика
export type IBoxUpdateData = Required<IBoxMain>;

// Интерфейс для запроса обновления ящика
export interface IBoxUpdate {
  boxOguid: string;
  data: IBoxUpdateData;
}

// Интерфейс обновления лого ящика
export interface IBoxLogoUpdate {
  boxOguid: string;
  data: FormData;
}

// Интерфейс создания инвайта пользователя
export interface IUserInviteCreate {
  lastName?: Nullable<string>;
  firstName?: Nullable<string>;
  middleName?: Nullable<string>;
  fullName: string;
  initials?: Nullable<string>;
  email?: Nullable<string>;
  position?: Nullable<string>;
  locale: string;
  timeZone: string;
  role: IBoxRole;
  phone: Nullable<string>;
}

// Интерфейс запроса создания инвайта пользователя
export type IUserInviteCreateRequest = Omit<IUserInviteCreate, 'phone'> & {
  phone?: Nullable<number>;
};

// Интерфейс обновления инвайта пользователя
export type IUserInviteUpdate = Omit<
  Required<IUserInviteCreateRequest>,
  'email' | 'phone'
>;

export interface IUserNotifications {
  isEmailNotification: boolean;
  notificationLevel: NotificationLevel;
}

export interface IUserUpdate extends IUserNotifications {
  position?: Nullable<string>;
  role: IBoxRole;
}

// Интерфейс пользователя в бизнес-ящике (get)
export interface IUser extends Required<IUserInviteCreateRequest>, IUserNotifications {
  created: string;
  userBoxOguid: string;
  createdBy: IEnvelopeUser;
}

// Интерфейс пользователя в бизнес-ящике (для формы)
export type IUserForm = Omit<IUser, 'phone'> & {
  phone?: Nullable<string>;
}

// Интерфейс приглашения пользователя в бизнес-ящик
export interface IUserInvite extends IUser {
  inviteOguid: string;
  notificationDatetime: string;
  notificationCount: number;
}

export interface IUserInviteResend {
  invites: string[];
}

/** Интерфейс обновления лого в стейте */
export interface ILogoUpd {
  boxOguid: string;
  logo: Nullable<string>;
}
